import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { getSourceIconStack } from './getSourceIcon.tsx';
import { SourceItemContent } from './SourceItem.tsx';
import type { Source } from './PopularQuestions.tsx';

export const SourcesSection = ({ sources }: { sources: readonly Source[] }) => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);

	const handleToggle = useCallback((event: React.MouseEvent) => {
		event.stopPropagation();
		setIsOpen((prev) => !prev);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const validSources = useMemo(() => sources.filter((source) => source.content != null), [sources]);

	if (validSources.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.triggerWrapper}>
			<Popup
				placement="bottom-start"
				isOpen={isOpen}
				onClose={handleClose}
				content={() => (
					<Box xcss={styles.popupContent}>
						<Inline
							space="space.0"
							spread="space-between"
							alignBlock="center"
							xcss={styles.popupHeader}
						>
							<Box xcss={styles.popupHeaderText}>
								<Text size="medium" weight="medium">
									{formatMessage(i18n.sourcesCount, { count: validSources.length })}
								</Text>
							</Box>
							<IconButton
								appearance="subtle"
								icon={(props) => <CrossIcon {...props} size="small" />}
								label={formatMessage(i18n.closeSources)}
								shape="circle"
								onClick={handleClose}
							/>
						</Inline>
						<Stack as="ul" xcss={styles.sourcesList} space="space.050">
							{validSources.map((source) => (
								<li key={source.content?.id} css={styles.sourceListItemWrapper}>
									<Pressable
										xcss={styles.sourceListItem}
										onClick={(e) => {
											e.stopPropagation();
											window.open(
												source.content?.links?.webui ?? '',
												'_blank',
												'noopener,noreferrer',
											);
										}}
									>
										<SourceItemContent source={source} />
									</Pressable>
								</li>
							))}
						</Stack>
					</Box>
				)}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						xcss={cx(styles.sourcesButton, isOpen && styles.sourcesButtonSelected)}
						onClick={handleToggle}
					>
						<Inline space="space.075" alignBlock="center">
							{getSourceIconStack(validSources)}
							<Box>{formatMessage(i18n.sourcesCount, { count: validSources.length })}</Box>
						</Inline>
					</Pressable>
				)}
			/>
		</Box>
	);
};

const i18n = defineMessages({
	sourcesCount: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-count',
		defaultMessage: '{count, plural, one {# Source} other {# Sources}}',
		description: 'Button label showing the number of sources for a popular question.',
	},
	closeSources: {
		id: 'space-overview.smart-space-overview.popular-questions.close-sources',
		defaultMessage: 'Close sources',
		description: 'Accessible label for the button to close the sources popup.',
	},
});

const styles = cssMap({
	triggerWrapper: {
		display: 'inline-block',
		flexShrink: 0,
	},

	sourcesButton: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		height: '30px',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	sourcesButtonSelected: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	popupContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
		width: '330px',
		maxHeight: '400px',
		overflowY: 'auto',
		borderRadius: token('radius.xlarge'),
	},
	popupHeader: {
		marginBottom: token('space.025'),
	},
	popupHeaderText: {
		paddingLeft: token('space.100'),
	},
	sourcesList: {
		paddingLeft: token('space.0'),
		listStyle: 'none',
		margin: 0,
	},
	sourceListItemWrapper: {
		listStyle: 'none',
	},
	sourceListItem: {
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		width: '100%',
		textAlign: 'left',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});
